import React, { useState, useMemo, useEffect } from "react";
import { observer } from "mobx-react-lite";

import {
  ViewSewerObject,
  ViewSewerObjectFromAssignedProject,
} from "../../../services/authorization/Permissions";
import PageLayout from "../../_layout/PageLayout";
import SewerObjectsMap from "../../../components/map/SewerObjectsMap";
import { ActionsSection } from "../../../components/data-components/sections/DataSections";
import { CurrentNodeStorageModel } from "../../../services/sewer-object/CurrentNodeStorage";
import { NodeMenuItemsModel } from "./NodeMenuItems";
import { SewerObjectsMapStorageModel } from "../../../services/sewer-object/SewerObjectsMapStorage";
import useUrlParams from "../../../services/url/UrlParamsService";
import { api } from "../../../services/api/ApiService";
import { useHistory } from "react-router-dom";
import useRouteService from "../../../services/route-service/RouteService";
import { EntityTypeFromPath } from "../../../services/breadcrumbs/EntityTypeFromPathModel";
import { breadcrumbsStorage } from "../../../services/breadcrumbs/BreadcrumbsStorage";

export default observer(function ViewNodeOnMap() {
  const history = useHistory();
  const urlParamsService = useUrlParams();
  const { isUserOnProjects } = useRouteService();
  const nodeId = useMemo(() => urlParamsService.getNodeId()!, [urlParamsService]);

  const [currentNodeStorage] = useState(() =>
    CurrentNodeStorageModel.create(
      {
        nodeId: nodeId,
        parentEntityFromPath: {
          id: urlParamsService.getSewerObjectParentId()!,
          type: isUserOnProjects() ? EntityTypeFromPath.Project : EntityTypeFromPath.Job,
        },
      },
      { api }
    )
  );

  const [nodeMenuItems] = useState(() =>
    NodeMenuItemsModel.create(
      {
        nodeId: nodeId,
        parentIdFromPath: currentNodeStorage.parentEntityFromPath.id,
      },
      { api }
    )
  );

  const [sewerObjectsMapStorage] = useState(() =>
    SewerObjectsMapStorageModel.create({ inputSewerObjects: [nodeId], autoLoad: false }, { api })
  );

  const menuItems = {
    staticItems: nodeMenuItems.getStaticMenuItems(),
  };

  useEffect(() => {
    async function loadBreadcrumbsData() {
      breadcrumbsStorage.setEntityNames([
        {
          entity: currentNodeStorage.parentEntityFromPath.type,
          name: await currentNodeStorage.getParentEntityName(
            currentNodeStorage.currentNode.project.name
          ),
        },
        {
          entity: EntityTypeFromPath.SewerObject,
          name: currentNodeStorage.currentNode.name,
        },
      ]);

      breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
    }

    loadBreadcrumbsData();
  }, [currentNodeStorage, currentNodeStorage.currentNode, history.location.pathname]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[ViewSewerObject, ViewSewerObjectFromAssignedProject]}
      titleKey="ViewNodeOnMap.title"
      titleContext={{ nodeName: currentNodeStorage.currentNode.name }}
      id="view-node-on-map"
      menuItems={menuItems}
      isLoading={currentNodeStorage.isLoading || sewerObjectsMapStorage.isDataLoading}
    >
      {nodeId && !currentNodeStorage.currentNode.hasGeometry && (
        <ActionsSection noBorder warningKey="ViewNodeOnMap.noGeometryWarning" />
      )}
      {nodeId && currentNodeStorage.currentNode.hasGeometry && (
        <div className="map-page-container">
          <SewerObjectsMap sewerObjectsMapStorage={sewerObjectsMapStorage} fullPage />
        </div>
      )}
    </PageLayout>
  );
});
